// File: app/javascript/controllers/name_search_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static values = {
        url: String,
        minChars: { type: Number, default: 2 },
        delay: { type: Number, default: 300 },
        // Optional CSS selectors to target fields to populate on selection
        firstFieldSelector: String,
        lastFieldSelector: String
    }

    static targets = ["results", "field"]

    connect() {
        console.log("name-search: connected", {
            hasUrlValue: this.hasUrlValue,
            urlValue: this.urlValue
        })
        this.abortController = null
        this._debounced = this._debounce(this._fetchResults.bind(this), this.delayValue)
    }

    disconnect() {
        console.log("name-search: disconnected")
        this._abortActive()
    }

    onInput(event) {
        const query = event.target.value.trim()
        console.log("name-search: onInput", { query })
        if (query.length < this.minCharsValue) {
            this._clearResults()
            return
        }
        this._debounced(query)
    }

    async _fetchResults(query) {
        if (!this.hasUrlValue || !this.urlValue) {
            console.warn("name-search: No URL configured via data-name-search-url-value")
            return
        }

        this._abortActive()
        this.abortController = new AbortController()

        try {
            const url = new URL(this.urlValue, window.location.origin)
            url.searchParams.set("q", query)
            console.log("name-search: fetching", url.toString())

            const res = await fetch(url.toString(), {
                headers: { "Accept": "application/json" },
                signal: this.abortController.signal
            })

            if (!res.ok) {
                throw new Error(`Search request failed: ${res.status}`)
            }

            const data = await res.json()
            this._renderResults(Array.isArray(data) ? data : (data.results || []))
        } catch (err) {
            if (err.name === "AbortError") return
            console.error("name-search: fetch error", err)
            this._clearResults()
        }
    }

    _renderResults(items) {
        if (!this.hasResultsTarget) return
        if (!items || items.length === 0) {
            this.resultsTarget.innerHTML = ""
            return
        }

        const normalized = items.map(item => {
            if (typeof item === "string") return { label: item, value: item }
            if (item && typeof item === "object") {
                const label = item.full_name || item.name || item.label || `${item.last_name || ""} ${item.first_name || ""}`.trim()
                const value = item.value || label
                return { label, value, raw: item }
            }
            return { label: String(item), value: String(item) }
        })

        this.resultsTarget.innerHTML = `
      <ul class="list-group">
        ${normalized.map(i => `<li class="list-group-item name-search-item" data-value="${this._escape(i.value)}">${this._escape(i.label)}</li>`).join("")}
      </ul>
    `

        this.resultsTarget.querySelectorAll(".name-search-item").forEach(li => {
            li.addEventListener("click", () => {
                const value = li.getAttribute("data-value") || ""
                if (this.hasFieldTarget) {
                    this.fieldTarget.value = value
                    this.fieldTarget.dispatchEvent(new Event("input", { bubbles: true }))
                }

                // Populate form fields (e.g., first_name, last_name)
                this._populateFormFields(value)

                this._clearResults()
                this.element.dispatchEvent(new CustomEvent("name-search:selected", {
                    detail: { value }, bubbles: true
                }))
            }, { once: true })
        })
    }

    _populateFormFields(fullName) {
        const { first, last } = this._splitName(fullName)

        const firstEl = this._resolveFieldElement("first")
        const lastEl  = this._resolveFieldElement("last")

        if (firstEl) {
            this._setValue(firstEl, first)
        }
        if (lastEl) {
            this._setValue(lastEl, last)
        }
    }

    _splitName(label) {
        const s = String(label || "").trim().replace(/\s+/g, " ")
        if (!s) return { first: "", last: "" }

        // Prefer "LAST, FIRST M" pattern if present
        if (s.includes(",")) {
            const parts = s.split(",")
            const last = parts[0].trim()
            const first = parts.slice(1).join(" ").trim()
            return { first, last }
        }

        const tokens = s.split(" ")
        if (tokens.length === 1) {
            // Fallback: assume first name only
            return { first: tokens[0], last: "" }
        }

        // Common FAA format "LAST FIRST [MIDDLE...]"
        const last = tokens[0]
        const first = tokens.slice(1).join(" ")
        return { first, last }
    }

    _resolveFieldElement(kind) {
        // Allow explicit selectors via data values
        if (kind === "first" && this.hasFirstFieldSelectorValue) {
            return document.querySelector(this.firstFieldSelectorValue)
        }
        if (kind === "last" && this.hasLastFieldSelectorValue) {
            return document.querySelector(this.lastFieldSelectorValue)
        }

        // Sensible Devise defaults
        const defaults = kind === "first"
            ? ['input[name="user[first_name]"]', "#user_first_name"]
            : ['input[name="user[last_name]"]', "#user_last_name"]

        for (const sel of defaults) {
            const el = document.querySelector(sel)
            if (el) return el
        }
        return null
    }

    _setValue(el, value) {
        if (!el) return
        el.value = value || ""
        // Trigger change/input for Rails/UJS/Stimulus/Validations to react
        el.dispatchEvent(new Event("input", { bubbles: true }))
        el.dispatchEvent(new Event("change", { bubbles: true }))
    }

    _clearResults() {
        if (this.hasResultsTarget) {
            this.resultsTarget.innerHTML = ""
        }
    }

    _abortActive() {
        if (this.abortController) {
            try { this.abortController.abort() } catch (_) {}
            this.abortController = null
        }
    }

    _debounce(fn, wait) {
        let t = null
        return (...args) => {
            clearTimeout(t)
            t = setTimeout(() => fn(...args), wait)
        }
    }

    _escape(str) {
        return String(str)
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
    }
}